import * as base64 from 'base-64';
import { browserHistory } from 'react-router';
import DOMPurify from 'dompurify';

import { IMAGE_SERVICE_URL } from 'app/config';
import { authorities as dealerAuthorities } from 'app/dealer/constants';
import { monthsDiff } from 'app/shared/utils/utils';

export const hasAuthority = (authorities = [], requiredAuthorities = []) => {
  return (
    requiredAuthorities.filter(
      authority => authorities.indexOf(authority) !== -1,
    ).length > 0
  );
};

export const canManageDealer = (authorities = []) => {
  const {
    MANAGE_DEALER,
    MANAGE_MY_DEALERS,
    MANAGE_MY_NETWORK,
  } = dealerAuthorities;

  return (
    authorities &&
    (authorities.indexOf(MANAGE_DEALER) >= 0 ||
      authorities.indexOf(MANAGE_MY_DEALERS) >= 0 ||
      authorities.indexOf(MANAGE_MY_NETWORK) >= 0)
  );
};

export const canCreateDealer = (authorities = []) => {
  const { CREATE_DEALER, MANAGE_MY_NETWORK } = dealerAuthorities;

  return (
    authorities &&
    (authorities.indexOf(CREATE_DEALER) >= 0 ||
      authorities.indexOf(MANAGE_MY_NETWORK) >= 0)
  );
};

export const canManageNetwork = (authorities = []) => {
  const { MANAGE_MY_NETWORK } = dealerAuthorities;

  return authorities && authorities.indexOf(MANAGE_MY_NETWORK) >= 0;
};

export const hasNoAuthority = (authorities = []) => authorities.length === 0;

export const checkForError = (response, userLoggedIn = false) => {
  return new Promise((resolve, reject) => {
    if (response.ok) {
      resolve(response);
    } else {
      const { status, statusText } = response;
      const payload = {
        status,
        message: statusText === '' ? 'generic error' : statusText,
      };

      if (status === 401 || status === 403) {
        if (userLoggedIn) {
          // kick them out once
          browserHistory.push('/dealer/logout');
        }

        return reject(response);
      }

      response.text().then(body => {
        let bodyContent = null;

        try {
          bodyContent = JSON.parse(body);
        } catch (error) {
          // json parse failed, just pass through the body
        }
        if (bodyContent && bodyContent.message) {
          payload.message = bodyContent.message;
        }
        payload.body = body;
        reject(payload);
      });
    }
  });
};

export const splitIban = (iban = '') => {
  const chars = [];

  [...iban].forEach((char, idx) => {
    if ((idx + 1) % 4) {
      chars.push(char);
    } else {
      chars.push(`${char} `);
    }
  });

  return chars.join('');
};

export const getNetworkTrackingPropertyGroup = network => {
  const { id } = network;

  return {
    network_id: id,
  };
};

export const getLeadTrackingPropertyGroup = lead => {
  const {
    billingState,
    createdAt,
    lead: {
      customer: { fullName },
      id,
      type,
    },
    saleState,
  } = lead;

  return {
    lead_has_name: Boolean(fullName && fullName.length),
    lead_id: id,
    lead_type: type,
    lead_sale_status: saleState,
    lead_billing_status: billingState,
    lead_date: createdAt,
  };
};

export const getListingTrackingPropertyGroup = listing => {
  const {
    id: listing_id,
    make: { id: make_id },
    model: { id: model_id },
  } = listing;

  return {
    listing_id,
    make_id,
    model_id,
  };
};

export const getDealerTrackingPropertyGroup = dealer => {
  const { id } = dealer;

  return {
    dealer_id: id,
  };
};

export const getFilterObject = option => {
  return {
    category: option.category,
    value: option.value,
    uuid: `${option.category}:${option.value}`,
    label: option.label || `${option.value}`,
    operator: 'and',
    parent: option.parent || null,
    direction: option.direction || null,
  };
};

/**
 * @typedef {Object} FilterCategory
 * @property {string} category
 * @property {string|number|null} value
 * @property {string} uuid
 * @property {string} label
 * @property {string} operator
 * @property {string|null} parent
 */

/**
 * returns the sortBy/sortDirection string for the post body
 * @param {FilterCategory[]} filterCategory the category of the filter reducer e.g. makes:[object, object]
 * @returns {String} returns a key=value string
 */
export const getSortForPostBody = filterCategory => {
  return `${filterCategory[0].value},${filterCategory[0].direction}`;
};

export const getPostBodyFromFilters = filterState => {
  const parameters = {
    filter: {},
    sort: [],
  };
  const filterSet = new Set(Object.keys(filterState));
  const apiIncludes = [
    'allowEmptyFullName',
    'billable',
    'billingState',
    'canceled',
    'createdAt',
    'dealer',
    'fullTextSearch',
    'partialExternalId',
    'onlyMetadata',
    'page',
    'saleState',
    'sortBy',
    'type',
    'wasReported',
  ];

  const mappingSet = new Set(apiIncludes);
  const commonSet = new Set(
    [...mappingSet].filter(item => filterSet.has(item)),
  );

  commonSet.forEach(filterCategoryName => {
    if (filterState[filterCategoryName].length === 0) {
      return;
    }

    let filterValue;

    if (filterCategoryName === 'page') {
      parameters.page = filterState.page[0].value;
    } else if (
      filterCategoryName === 'billable' ||
      filterCategoryName === 'canceled' ||
      filterCategoryName === 'createdAt' ||
      filterCategoryName === 'fullTextSearch' ||
      filterCategoryName === 'partialExternalId' ||
      filterCategoryName === 'onlyMetadata' ||
      filterCategoryName === 'wasReported'
    ) {
      filterValue = filterState[filterCategoryName][0].value;
    } else if (filterCategoryName === 'sortBy') {
      parameters.sort.push(getSortForPostBody(filterState[filterCategoryName]));
    } else {
      filterValue = filterState[filterCategoryName].map(item => item.value);
    }
    if (filterValue !== undefined) {
      parameters.filter[filterCategoryName] = filterValue;
    }
  });

  return parameters;
};

export const getMonthRangeAsFilter = (startOffset = 0, endOffset = 0) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month + startOffset, 1);
  const lastDay = new Date(year, month + endOffset + 1, 0, 23, 59, 59, 999);

  return {
    from: firstDay,
    to: lastDay,
  };
};

export const getMonthOffsetByDates = (d1, d2) => {
  let months;

  months = (d2.getFullYear() - d1.getFullYear()) * 12;
  months -= d1.getMonth();
  months += d2.getMonth();

  return months;
};

export const getMonthRangeOffsetsByDates = (startDate, endDate) => {
  return {
    from: getMonthOffsetByDates(new Date(), startDate),
    to: getMonthOffsetByDates(new Date(), endDate),
  };
};

const isNewCheckboxFilter = (filters, newFilter) => {
  return filters.filter(f => f.uuid === newFilter.uuid).length === 0;
};

const removeCheckboxFilter = (filters, newFilter) => {
  return filters.filter(f => f.uuid !== newFilter.uuid);
};

const getCheckboxFilter = (currentFilters = [], newFilter) => {
  let newFilters = [...currentFilters];

  if (newFilter.value === null) {
    newFilters = [];
  } else if (isNewCheckboxFilter(newFilters, newFilter)) {
    newFilters.push(newFilter);
  } else {
    newFilters = removeCheckboxFilter(newFilters, newFilter);
  }

  return newFilters;
};

const getRangeFilter = (currentFilters = [], newFilter) => {
  let newFilters = [...currentFilters];

  if (newFilter.value === null) {
    newFilters = [];
  } else {
    newFilters.push(newFilter);
  }

  return newFilters;
};

const getRadioFilter = (currentFilters = [], newFilter) => {
  let newFilters = [...currentFilters];

  if (newFilter.value === null) {
    newFilters = [];
  } else {
    newFilters.push(newFilter);
  }

  return newFilters;
};

export const createFiltersFromArray = (filters, state, filterByCategories) => {
  const newFilters = {};
  let hasPage = false;
  filters.forEach(filter => {
    const { category } = filter;
    hasPage = hasPage || category === 'page';
    if (filterByCategories.checkBox.indexOf(category) > -1) {
      newFilters[category] = getCheckboxFilter(
        newFilters[category] || state[category],
        filter,
      );
    } else if (filterByCategories.range.indexOf(category) > -1) {
      newFilters[category] = getRangeFilter(newFilters[category], filter);
    } else if (filterByCategories.radio.indexOf(category) > -1) {
      newFilters[category] = getRadioFilter(newFilters[category], filter);
    }
  });

  if (!hasPage) {
    newFilters.page = [getFilterObject({ category: 'page', value: 0 })];
  }

  return newFilters;
};

export const decodeJWTPayload = token => {
  const base64Url = token.split('.')[1];
  const b64 = base64Url.replace('-', '+').replace('_', '/');

  return JSON.parse(base64.decode(b64));
};

export const years = (startDate = new Date(), endDate = new Date()) => {
  const yearsList = [];
  for (let i = startDate.getFullYear(); i <= endDate.getFullYear(); i += 1) {
    yearsList.push(i);
  }
  return yearsList;
};

export const months = (startDate = new Date(), endDate = new Date()) => {
  const numberOfMonths = monthsDiff(startDate, endDate);
  const dates = [];

  if (numberOfMonths === 0) {
    return [];
  }

  for (let i = 0; i <= numberOfMonths; i += 1) {
    const date = new Date(startDate);
    const month = date.getMonth();
    const toPush = new Date(date.setMonth(month + i));

    dates.push(toPush);
  }

  return dates;
};

export const yearMonthDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;

  return `${year}-${month < 10 ? `0${month}` : month}`;
};

export const getMonthsForLocale = ({ locale, monthFormat = 'short' }) => {
  const format = new Intl.DateTimeFormat(locale, { month: monthFormat });
  const months = [];
  for (let month = 0; month < 12; month += 1) {
    const testDate = new Date(Date.UTC(2000, month, 1, 0, 0, 0));
    months.push(format.format(testDate));
  }
  return months;
};

// TODO: update these all the
// image utility functions below
// so they are more reusable
// note: smart thumbor image component will help us remove these functions
export const getImageAttrs = ({ width = 480, quality = 20, src, alt }) => ({
  url: `${IMAGE_SERVICE_URL}/unsafe/${width}x/filters:quality(${quality}):no_upscale()/${src}`,
  alt,
});

export const getPlaceholderImage = ({ category }) =>
  `/assets/specials/${category}.svg`;

export const createImageLink = vehicle => {
  const { images, categories } = vehicle;
  const width = 480;
  const quality = 90;
  const hasImages = Array.isArray(images) && images.length > 0;
  const hasCategories = Array.isArray(categories) && categories.length > 0;
  const alt = `${vehicle.make.displayName} ${vehicle.model.displayName} ${vehicle.model.trim}`.trim();
  if (!hasImages) {
    return {
      url: hasCategories
        ? getPlaceholderImage({
            category: vehicle.categories[0],
          })
        : '',
      alt,
    };
  }
  return getImageAttrs({ width, quality, src: images[0], alt });
};

export function getSanitizedInnerHTML(content) {
  return { __html: DOMPurify.sanitize(content) };
}

/**
 * @description
 * function takes an array, and a value to toggle in the array
 *
 * if the value exists in the array, it will be removed
 * else it will be added to the array
 * @param {Array<string|number>} arr - array of primitive values
 * @param {string|number} value - value to toggle
 */
export const toggleValueInArray = (arr, value) => {
  const collection = new Set(arr);

  if (collection.size === collection.add(value).size) {
    collection.delete(value);
  }
  return [...collection];
};

export const generateApiUrl = url =>
  `${process.env.REACT_APP_MOCK_SERVER ? 'http://localhost:5000' : ''}${url}`;

/**
 * @description
 * small helper for prettier state
 * objects using `useSelector` hook
 * from `react-redux`
 *
 * follows the style of `createStructuredSelector`
 * from `reselect`
 *
 */
export const structuredSelector = selectors => {
  const entries = Object.entries(selectors);
  return state =>
    entries.reduce((acc, [stateProp, selector]) => {
      acc[stateProp] = selector(state);
      return acc;
    }, {});
};

export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const keyValue = item[key];
    (result[keyValue] || (result[keyValue] = [])).push(item);
    return result;
  }, {});
};
